import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';

/* ═══════════════════════════════════════════════════════════
   Container Drilldown Modal
   A cross-database trace of a single container:
     • Azure SQL InboundBookings + attachments/photos
     • DB2 PWAEKOP / PWAEPOS goods receipt + line items
     • DB2 PBESTLO currently-stored items
     • DB2 PBESTSS truck timings (arrival/departure)
     • Connected customer + warehouse metadata
   ═══════════════════════════════════════════════════════════ */

export interface ContainerDrilldownModalProps {
  containerNumber: string | null;
  open: boolean;
  onClose: () => void;
}

interface TraceData {
  containerNumber: string;
  booking: any | null;
  customer: any | null;
  warehouse: any | null;
  grHeader: any | null;
  grLines: any[];
  truckEvents: any[];
  storedItems: any[];
  attachments: any[];
  timeline: Array<{
    ts: string | null;
    kind: string;
    title: string;
    detail?: string;
    source?: string;
  }>;
  summary: {
    totalLines: number;
    totalQty: number;
    totalPallets: number;
    storedCount: number;
    attachmentCount: number;
  };
}

type TabKey = 'timeline' | 'items' | 'attachments' | 'context';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'timeline', label: 'Timeline', icon: '🕒' },
  { key: 'items', label: 'Items', icon: '📦' },
  { key: 'attachments', label: 'Documents & Photos', icon: '📎' },
  { key: 'context', label: 'Customer + Warehouse', icon: '🏭' },
];

/* ── helpers ───────────────────────────────────────────── */
const fmtDate = (v?: string | null) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(v);
  }
};

const fmtNum = (n: number | null | undefined) =>
  typeof n === 'number' ? n.toLocaleString() : '—';

const isImageName = (name: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name || '');

/* ═══════════════════════════════════════════════════════════ */
const ContainerDrilldownModal: React.FC<ContainerDrilldownModalProps> = ({
  containerNumber,
  open,
  onClose,
}) => {
  const { bgModeName } = useTheme();
  const isDark = bgModeName === 'dark';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TraceData | null>(null);
  const [tab, setTab] = useState<TabKey>('timeline');

  /* ── palette driven by theme ──────────────────────────── */
  const c = useMemo(() => ({
    bg: isDark ? '#0b1220' : '#f6f8fc',
    panel: isDark ? '#111827' : '#ffffff',
    border: isDark ? '#1f2937' : '#e5e7eb',
    text: isDark ? '#f3f4f6' : '#111827',
    sub: isDark ? '#9ca3af' : '#6b7280',
    accent: '#6366f1',
    accentSoft: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.10)',
    green: '#22c55e',
    amber: '#f59e0b',
    red: '#ef4444',
    blue: '#3b82f6',
  }), [isDark]);

  /* ── fetch on open ────────────────────────────────────── */
  useEffect(() => {
    if (!open || !containerNumber) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setTab('timeline');

    api.getContainerTrace(containerNumber)
      .then((res) => { if (!cancelled) setData(res as TraceData); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message || 'Failed to load container trace';
        setError(String(msg));
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, containerNumber]);

  /* ── keyboard close ──────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  /* ── styling primitives ───────────────────────────────── */
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
    zIndex: 9990, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  };
  const panel: React.CSSProperties = {
    width: 'min(1100px, 100%)', height: 'min(780px, 92vh)',
    background: c.panel, color: c.text,
    borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', border: `1px solid ${c.border}`,
  };
  const header: React.CSSProperties = {
    padding: '18px 22px', borderBottom: `1px solid ${c.border}`,
    display: 'flex', alignItems: 'center', gap: 16,
    background: isDark ? 'linear-gradient(135deg,#111827,#1f2937)' : 'linear-gradient(135deg,#eef2ff,#ffffff)',
  };
  const tabsBar: React.CSSProperties = {
    display: 'flex', gap: 6, padding: '10px 18px 0',
    borderBottom: `1px solid ${c.border}`, background: c.panel,
    overflowX: 'auto',
  };
  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '10px 14px', border: 'none', background: 'transparent',
    color: active ? c.accent : c.sub, fontWeight: 600, fontSize: 13,
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    whiteSpace: 'nowrap',
  });
  const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: 20 };
  const chip = (bg: string, color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 999,
    background: bg, color, fontSize: 11, fontWeight: 600,
    border: `1px solid ${color}33`,
  });
  const card: React.CSSProperties = {
    background: isDark ? '#0f172a' : '#fafbff',
    border: `1px solid ${c.border}`, borderRadius: 12,
    padding: 14, marginBottom: 12,
  };

  /* ── sub-render helpers ───────────────────────────────── */
  const renderSummaryStrip = () => {
    if (!data) return null;
    const s = data.summary;
    const items = [
      { k: 'GR Lines', v: s.totalLines, color: c.blue },
      { k: 'Total Qty', v: s.totalQty, color: c.green },
      { k: 'Pallets', v: s.totalPallets, color: c.accent },
      { k: 'Stored Now', v: s.storedCount, color: c.amber },
      { k: 'Docs + Photos', v: s.attachmentCount, color: c.red },
    ];
    return (
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', marginBottom: 16 }}>
        {items.map((it) => (
          <div key={it.k} style={{ ...card, marginBottom: 0, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: c.sub, letterSpacing: 0.5, textTransform: 'uppercase' }}>{it.k}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: it.color, marginTop: 4 }}>{fmtNum(it.v)}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderTimeline = () => {
    if (!data) return null;
    if (!data.timeline?.length) {
      return <div style={{ color: c.sub, padding: 24, textAlign: 'center' }}>No timeline events recorded yet.</div>;
    }
    const colorFor = (kind: string) => {
      if (kind.includes('booking')) return c.blue;
      if (kind.includes('arriv')) return c.amber;
      if (kind.includes('depart')) return c.accent;
      if (kind.includes('gr') || kind.includes('receipt')) return c.green;
      return c.sub;
    };
    return (
      <div style={{ position: 'relative', paddingLeft: 24 }}>
        <div style={{
          position: 'absolute', left: 7, top: 4, bottom: 4, width: 2,
          background: c.border,
        }} />
        {data.timeline.map((ev, i) => {
          const dot = colorFor(ev.kind);
          return (
            <div key={i} style={{ position: 'relative', marginBottom: 14 }}>
              <div style={{
                position: 'absolute', left: -24, top: 4,
                width: 16, height: 16, borderRadius: 999,
                background: dot, boxShadow: `0 0 0 3px ${c.panel}`,
                border: `2px solid ${c.panel}`,
              }} />
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{ev.title}</div>
                  <div style={{ fontSize: 12, color: c.sub }}>{fmtDate(ev.ts)}</div>
                </div>
                {ev.detail && (
                  <div style={{ fontSize: 13, color: c.sub, marginTop: 4 }}>{ev.detail}</div>
                )}
                {ev.source && (
                  <div style={{ marginTop: 6 }}>
                    <span style={chip(c.accentSoft, c.accent)}>{ev.source}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderItems = () => {
    if (!data) return null;

    const section = (title: string, rows: any[], cols: Array<{ k: string; label: string; align?: 'left' | 'right' }>) => (
      <div style={{ marginBottom: 22 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 14, color: c.text }}>{title} <span style={{ color: c.sub, fontWeight: 400 }}>({rows.length})</span></h4>
        {rows.length === 0 ? (
          <div style={{ color: c.sub, fontStyle: 'italic', padding: 10 }}>No records.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${c.border}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: isDark ? '#1f2937' : '#f3f4f6' }}>
                  {cols.map((col) => (
                    <th key={col.k} style={{
                      textAlign: col.align || 'left', padding: '10px 12px',
                      fontWeight: 600, color: c.sub, borderBottom: `1px solid ${c.border}`,
                    }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${c.border}` }}>
                    {cols.map((col) => (
                      <td key={col.k} style={{
                        padding: '9px 12px',
                        textAlign: col.align || 'left',
                        color: c.text,
                      }}>
                        {row[col.k] === null || row[col.k] === undefined || row[col.k] === ''
                          ? '—'
                          : typeof row[col.k] === 'number'
                            ? row[col.k].toLocaleString()
                            : String(row[col.k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );

    return (
      <>
        {section('Goods Receipt — Line Items', data.grLines, [
          { k: 'ITEM_CODE', label: 'Item' },
          { k: 'ITEM_DESCRIPTION', label: 'Description' },
          { k: 'BATCH', label: 'Batch' },
          { k: 'QTY', label: 'Qty', align: 'right' },
          { k: 'UOM', label: 'UoM' },
          { k: 'PALLETS', label: 'Pallets', align: 'right' },
          { k: 'EXPIRY_DATE', label: 'Expiry' },
        ])}
        {section('Currently Stored', data.storedItems, [
          { k: 'ITEM_CODE', label: 'Item' },
          { k: 'BATCH', label: 'Batch' },
          { k: 'LOCATION', label: 'Location' },
          { k: 'QTY', label: 'Qty', align: 'right' },
          { k: 'UOM', label: 'UoM' },
          { k: 'STATUS', label: 'Status' },
        ])}
        {section('Truck Events', data.truckEvents, [
          { k: 'EVENT', label: 'Event' },
          { k: 'TIMESTAMP', label: 'When' },
          { k: 'DOCK', label: 'Dock' },
          { k: 'TRUCK_NUMBER', label: 'Truck' },
          { k: 'DRIVER', label: 'Driver' },
        ])}
      </>
    );
  };

  const renderAttachments = () => {
    if (!data) return null;
    if (!data.attachments?.length) {
      return <div style={{ color: c.sub, padding: 24, textAlign: 'center' }}>No documents or photos attached to this container.</div>;
    }
    return (
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {data.attachments.map((att: any, i: number) => {
          const name: string = att.file_name || att.filename || att.name || `Attachment ${i + 1}`;
          const url: string | undefined = att.url || att.file_url || att.path;
          const image = isImageName(name);
          return (
            <a key={i} href={url || '#'} target="_blank" rel="noreferrer" style={{
              ...card, textDecoration: 'none', color: c.text,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {image && url ? (
                <img src={url} alt={name} style={{
                  width: '100%', aspectRatio: '4/3', objectFit: 'cover',
                  borderRadius: 8, background: c.border,
                }}/>
              ) : (
                <div style={{
                  width: '100%', aspectRatio: '4/3', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: c.accentSoft, color: c.accent, fontSize: 34,
                }}>📄</div>
              )}
              <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{name}</div>
              <div style={{ fontSize: 11, color: c.sub }}>{att.uploaded_at ? fmtDate(att.uploaded_at) : att.category || ''}</div>
            </a>
          );
        })}
      </div>
    );
  };

  const renderContext = () => {
    if (!data) return null;
    const cust = data.customer || {};
    const wh = data.warehouse || {};
    const booking = data.booking || {};
    const gr = data.grHeader || {};

    const kv = (label: string, value: any) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px dashed ${c.border}` }}>
        <span style={{ color: c.sub, fontSize: 12 }}>{label}</span>
        <span style={{ color: c.text, fontSize: 13, fontWeight: 500, textAlign: 'right' }}>
          {value === null || value === undefined || value === '' ? '—' : String(value)}
        </span>
      </div>
    );

    return (
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Customer</h4>
          {kv('Customer Code', cust.customer_code || cust.code)}
          {kv('Customer Name', cust.customer_name || cust.name)}
          {kv('BMFIRM', cust.bmfirm)}
          {kv('Contact Person', cust.contact_person)}
          {kv('Phone', cust.contact_phone)}
          {kv('Email', cust.contact_email)}
        </div>
        <div style={card}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Warehouse</h4>
          {kv('Code', wh.warehouse_code || wh.code)}
          {kv('Name', wh.warehouse_name || wh.name)}
          {kv('Zone', wh.zone)}
          {kv('Address', wh.address)}
        </div>
        <div style={card}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Booking</h4>
          {kv('Reference', booking.booking_reference)}
          {kv('Status', booking.status)}
          {kv('Expected Date', booking.expected_date)}
          {kv('Time Slot', booking.time_slot)}
          {kv('Dock', booking.dock_preference || booking.dock_assigned)}
          {kv('Truck #', booking.truck_number)}
          {kv('Driver', booking.driver_name)}
          {kv('Container #', booking.container_number)}
        </div>
        <div style={card}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Goods Receipt (DB2)</h4>
          {kv('GR #', gr.GR_NUMBER || gr.BKBELN)}
          {kv('GR Date', gr.GR_DATE)}
          {kv('PO #', gr.PO_NUMBER)}
          {kv('Receiver', gr.RECEIVER)}
          {kv('Total Lines', gr.TOTAL_LINES)}
          {kv('Total Qty', gr.TOTAL_QTY)}
        </div>
      </div>
    );
  };

  /* ── composition ─────────────────────────────────────── */
  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div style={header}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: c.accentSoft, color: c.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>📦</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: c.sub, letterSpacing: 0.4, textTransform: 'uppercase' }}>Container Trace</div>
            <div style={{ fontSize: 20, fontWeight: 700, wordBreak: 'break-all' }}>{containerNumber || '—'}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {data?.booking?.status && (
              <span style={chip(c.accentSoft, c.accent)}>{data.booking.status}</span>
            )}
            {data?.warehouse?.warehouse_code && (
              <span style={chip(isDark ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.12)', c.green)}>
                {data.warehouse.warehouse_code}
              </span>
            )}
            {data?.customer?.customer_name && (
              <span style={chip(isDark ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.12)', c.blue)}>
                {data.customer.customer_name}
              </span>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            marginLeft: 8, background: 'transparent', border: `1px solid ${c.border}`,
            color: c.text, width: 36, height: 36, borderRadius: 10,
            cursor: 'pointer', fontSize: 18,
          }}>×</button>
        </div>

        {/* tabs */}
        <div style={tabsBar}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} style={tabBtn(tab === t.key)}>
              <span>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* body */}
        <div style={body}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: c.sub }}>
              <div style={{
                display: 'inline-block', width: 32, height: 32,
                border: `3px solid ${c.border}`, borderTopColor: c.accent,
                borderRadius: '50%', animation: 'spin 0.9s linear infinite',
              }} />
              <div style={{ marginTop: 12, fontSize: 13 }}>Tracing container across Azure SQL and DB2…</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {error && !loading && (
            <div style={{
              padding: 18, border: `1px solid ${c.red}66`,
              background: `${c.red}14`, color: c.red, borderRadius: 12,
            }}>
              <strong>Could not trace container.</strong>
              <div style={{ marginTop: 6, fontSize: 13 }}>{error}</div>
            </div>
          )}
          {!loading && !error && data && (
            <>
              {renderSummaryStrip()}
              {tab === 'timeline' && renderTimeline()}
              {tab === 'items' && renderItems()}
              {tab === 'attachments' && renderAttachments()}
              {tab === 'context' && renderContext()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContainerDrilldownModal;
